import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { HcpGroup, ProfileType } from '@ixicare/resources';
import { useStores } from '~store/utils';
import { useHcpGroups } from './useHcpGroups';

/**
 * Alert-mode for site observers — Watcherr employees and org-admin installers.
 * A normal HCP is always an active alarm handler; an observer defaults to
 * passive (view-only) and only becomes active by joining an HCP group.
 *
 * Membership is durable and site-scoped: joining a group puts you *on duty* for
 * that group's site until you explicitly leave. "Active" is then derived per
 * session — you're active while viewing your on-duty site, and passive (view
 * only) on any other site, without the membership changing. That keeps multiple
 * sessions consistent: nothing here mutates membership as a side effect of
 * navigating.
 *
 * Eligibility mirrors the backend's site-observer gate (`_is_site_observer`):
 * `can_view_site` plus either an internal employee or an installer scoped to an
 * organization.
 */
export function useObserverAlertMode() {
  const { auth, sites, permissions, alerts } = useStores();

  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;
  const isOrgInstaller = auth.isInstaller && !!auth.user?.permissionOrganizationId;
  const isObserver = permissions.has('can_view_site') && (isEmployee || isOrgInstaller);

  const selectedSiteUuid = sites.selectedSiteUuid ?? undefined;
  const hcpGroupId = auth.user?.hcpGroupId ?? null;

  // Groups at the viewed site — the options offered to start handling alarms.
  const { data: groups, isSuccess: groupsLoaded } = useHcpGroups(selectedSiteUuid, { staleTime: 30_000 });
  const groupList = React.useMemo(() => groups ?? [], [groups]);

  // A site with no groups can't segment, so there's nothing to join: handling
  // alarms there is a local preference over the observer feed they already get.
  // This describes the *viewed* site — it decides whether activating here takes
  // a group, not whether the user is on duty.
  const isUnsegmentedSite = groupsLoaded && groupList.length === 0;

  // The user's own group, fetched by id so we can resolve its site even when
  // that site isn't the one being viewed (that's the whole on-duty-elsewhere case).
  const { data: ownGroup, isSuccess: ownGroupLoaded } = useQuery({
    queryKey: ['hcpGroup', hcpGroupId],
    queryFn: () => HcpGroup.getHcpGroupById(hcpGroupId!),
    enabled: !!hcpGroupId,
    staleTime: 60_000,
  });

  const groupSiteUuid = React.useMemo(
    () => (ownGroup ? sites.sites.find((s) => s.id === ownGroup.siteId)?.uuid : undefined),
    [ownGroup, sites.sites]
  );
  // The site whose alarms this user is handling: their group's site, or the
  // unsegmented site they switched handling on for. Read from the store rather
  // than from the viewed site — otherwise browsing elsewhere would silently
  // drop them off duty and mute the alarms they're still responsible for.
  const noSegDutySiteUuid = alerts.noSegDutySite;
  const onDutySiteUuid = groupSiteUuid ?? noSegDutySiteUuid;

  // Until the group fetch and the sites list have both landed, "no duty site"
  // means "not known yet", not "off duty". Acting on the guess silences a real
  // on-duty user for the length of the round trip.
  const isDutyResolved = !hcpGroupId || (ownGroupLoaded && sites.loaded);
  const onDutySiteName = React.useMemo(
    () => (onDutySiteUuid ? sites.sites.find((s) => s.uuid === onDutySiteUuid)?.name : undefined),
    [onDutySiteUuid, sites.sites]
  );
  // Comes from the by-id fetch, not the viewed site's list, so it still resolves
  // while on duty elsewhere — where the group isn't among the viewed options.
  const onDutyGroupName = ownGroup?.hcpGroupName;

  // On duty *somewhere* (drives sound + the mute toggle) vs active *here*
  // (drives list scoping, the presence picker and whether actions are enabled).
  const isOnDuty = isObserver && !!onDutySiteUuid;
  const isActiveObserver = isOnDuty && onDutySiteUuid === selectedSiteUuid;
  const isPassiveObserver = isObserver && !isActiveObserver;
  const isOnDutyElsewhere = isOnDuty && !isActiveObserver;

  // Clear the site's bucket, reconnect the socket (so the backend re-resolves
  // which channels to feed), and refetch under the new mode. Runs after the
  // (un)assignment has committed and the profile has been refreshed.
  const refreshFeed = React.useCallback(() => {
    alerts.clearSite(selectedSiteUuid);
    alerts.reconnect();
    void alerts.fetchAlerts(selectedSiteUuid);
  }, [alerts, selectedSiteUuid]);

  /** Start handling alarms here. `groupId` is null on an unsegmented site, where
   *  there's no group to join and nothing about routing changes. */
  const activate = React.useCallback(
    async (groupId: string | null) => {
      const userId = auth.user?.id;
      if (userId === undefined) return;
      if (groupId === null) {
        if (selectedSiteUuid) alerts.setNoSegHandling(selectedSiteUuid, true);
        return;
      }
      await HcpGroup.assignHcpToHcpGroup(groupId, userId);
      await auth.fetchUser(userId);
      refreshFeed();
    },
    [alerts, auth, refreshFeed, selectedSiteUuid]
  );

  /** Back to view-only: leave the group, or switch off local handling on the
   *  unsegmented site it was switched on for — which may not be the one being
   *  viewed. */
  const deactivate = React.useCallback(async () => {
    if (noSegDutySiteUuid) {
      alerts.setNoSegHandling(noSegDutySiteUuid, false);
      return;
    }
    const userId = auth.user?.id;
    const groupId = auth.user?.hcpGroupId;
    if (userId === undefined || !groupId) return;
    await HcpGroup.unassignHcpFromHcpGroup(groupId, userId);
    await auth.fetchUser(userId);
    refreshFeed();
  }, [alerts, auth, noSegDutySiteUuid, refreshFeed]);

  return {
    isObserver,
    isOnDuty,
    isOnDutyElsewhere,
    isActiveObserver,
    isPassiveObserver,
    isDutyResolved,
    onDutySiteUuid,
    onDutySiteName,
    onDutyGroupName,
    selectedSiteUuid,
    groups: groupList,
    isUnsegmentedSite,
    activate,
    deactivate,
  };
}
